import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getApiClient } from '@/lib/api/client'
import { useAuth } from '@/providers/AuthProvider'

interface PermissionContextType {
  permissions: string[]
  loading: boolean
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
  refreshPermissions: () => Promise<void>
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined)

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const { isAuthenticated, tokens, isLoading: authLoading } = useAuth()

  const fetchPermissions = useCallback(async () => {
    // Don't fetch while auth is still loading — wait for it to settle
    if (authLoading) {
      return
    }

    if (!isAuthenticated || !tokens) {
      setPermissions([])
      setLoading(false)
      return
    }

    try {
      const response = await getApiClient().get<{ permissions: string[] }>('/users/me/permissions')
      setPermissions(response.data.permissions || [])
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
      setPermissions([])
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, tokens, authLoading])

  useEffect(() => {
    // Reset loading state when auth starts loading (e.g., on refresh)
    if (authLoading) {
      setLoading(true)
      return
    }
    void fetchPermissions()
  }, [fetchPermissions, authLoading])

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return permissions.includes(permission)
    },
    [permissions],
  )

  const hasAnyPermission = useCallback(
    (perms: string[]): boolean => {
      return perms.some((p) => permissions.includes(p))
    },
    [permissions],
  )

  const hasAllPermissions = useCallback(
    (perms: string[]): boolean => {
      return perms.every((p) => permissions.includes(p))
    },
    [permissions],
  )

  const refreshPermissions = useCallback(async () => {
    setLoading(true)
    await fetchPermissions()
  }, [fetchPermissions])

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const usePermissions = () => {
  const context = useContext(PermissionContext)
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider')
  }
  return context
}
